import { useState, type ReactNode } from "react";
import { useTranslations } from "next-intl";
import { apiErrorText } from "@/lib/api-error";
import { downloadApiFile } from "@/services/http/download";

type ApiDownloadButtonProps = {
  /** API path, e.g. `/v1/courses/${id}/certificate/download` (resolved against the API base). */
  path: string;
  /** Saved file name; defaults to the server's Content-Disposition name. */
  fileName?: string;
  className?: string;
  children: ReactNode;
  "data-testid"?: string;
};

/**
 * C1: the replacement for `<a href="/api/...">` on protected files. Downloads through
 * `downloadApiFile` (token + refresh), shows a translated error if the server refuses.
 */
export function ApiDownloadButton({
  path,
  fileName,
  className,
  children,
  "data-testid": testId,
}: ApiDownloadButtonProps) {
  const t = useTranslations();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleClick() {
    setBusy(true);
    setError(null);
    try {
      await downloadApiFile(path, fileName);
    } catch (err) {
      setError(apiErrorText(err, t, "errors.downloadFailed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className={className}
        data-testid={testId}
        disabled={busy}
        aria-busy={busy || undefined}
        onClick={() => void handleClick()}
      >
        {children}
      </button>
      {error ? (
        <span role="alert" className="ml-2 text-xs text-[var(--danger)]">
          {error}
        </span>
      ) : null}
    </>
  );
}
